import { get } from "lodash-es";

/**
 * @typedef {Object} FlagCondition
 * @property {string} [source] - Context bucket to read the compared value from, e.g. "flags",
 *   "app", "user", or a non-flags root registered in RfAdmin's `fieldSources` (e.g. "path_filter").
 * @property {string} [key] - Key to read within that bucket. Supports dot-paths for nested
 *   values (e.g. "users.is_limited") via lodash `get`.
 * @property {*} [eq] - Value the read value must equal for this condition to hold. A boolean
 *   `eq` is compared with `Boolean(value) === eq` (so an unset/legacy-truthy value still behaves
 *   like the old plain `flags.x`/`!flags.x` checks); any other `eq` type requires strict equality.
 * @property {FlagCondition[]} [all] - Sub-conditions that must all hold (AND).
 * @property {FlagCondition[]} [any] - Sub-conditions where at least one must hold (OR).
 */

/**
 * @typedef {Object} FlagConditionContext
 * @property {Object} flags - Current flag values, keyed by flag name.
 * @property {Object} [app] - App-level attributes that aren't flags (e.g. is_internal).
 * @property {Object} [user] - Current user attributes (e.g. isSuperAdmin).
 * @property {Object} [path_filter] - `app.path_filter` values (e.g. users.is_limited).
 */

/**
 * @typedef {Object} FlagDescriptor
 * @property {string} key - Flag name.
 * @property {FlagCondition} [visibleIf] - Whether the field should be rendered at all.
 * @property {FlagCondition} [disabledIf] - Whether the rendered control should be disabled.
 * @property {FlagCondition} [resetIf] - Whether the value should be forced back to resetValue.
 * @property {*} [resetValue] - Value to reset to when resetIf holds. Defaults to false.
 * @property {boolean} [coerceFalseIfUnset] - Whether this boolean flag should be saved as `false`
 *   instead of left `null`/`undefined` when falsy at save time.
 */

/**
 * Evaluates a flag condition tree against a context of flag/app/user values.
 * A missing condition is treated as always-true (no restriction).
 *
 * @param {FlagCondition} [condition] - Condition to evaluate.
 * @param {FlagConditionContext} context - Values available for comparison.
 * @returns {boolean} Whether the condition holds.
 * @example
 * evaluateCondition(
 *   { all: [
 *     { source: "flags", key: "elastic_search_metrics", eq: true },
 *     { source: "app", key: "is_internal", eq: true },
 *   ] },
 *   { flags: { elastic_search_metrics: true }, app: { is_internal: true } },
 * ); // => true
 */
export const evaluateCondition = (condition, context) => {
  if (!condition) return true;
  if (condition.all) return condition.all.every(sub => evaluateCondition(sub, context));
  if (condition.any) return condition.any.some(sub => evaluateCondition(sub, context));

  const bucket = context[condition.source ?? "flags"] ?? {};
  const value = get(bucket, condition.key);
  // A boolean eq (true or false) is coerced with Boolean(), matching the old code's plain
  // `flags.x`/`!flags.x` truthy checks — a legacy non-boolean truthy value (e.g. `1`, `"true"`)
  // still counts as "on", and an unset flag (`undefined`) still counts as "off". Non-boolean eq
  // values (numbers, strings) still require strict equality.
  return typeof condition.eq === "boolean"
    ? Boolean(value) === condition.eq
    : value === condition.eq;
};

/**
 * Collects the flag keys a condition tree reads from the "flags" bucket.
 * Building block for a future dependency-graph inspection of resetIf rules.
 *
 * @param {FlagCondition} [condition] - Condition to inspect.
 * @returns {string[]} Flag keys referenced with source "flags".
 * @example
 * collectFlagDependencies({
 *   all: [
 *     { key: "bloom_filter_enabled", eq: false },
 *     { source: "app", key: "is_internal", eq: true },
 *   ],
 * }); // => ["bloom_filter_enabled"]
 */
export const collectFlagDependencies = condition => {
  if (!condition) return [];
  if (condition.all || condition.any) {
    return (condition.all ?? condition.any).flatMap(collectFlagDependencies);
  }
  return (condition.source ?? "flags") === "flags" ? [condition.key] : [];
};
